import React from 'react';
import PropTypes from 'prop-types';

import Dropzone from 'react-dropzone';
import { ProgressBar } from 'react-bootstrap-5';

import { EditorState, convertToRaw, convertFromRaw, AtomicBlockUtils, ContentState, RichUtils } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';

import './wysiwyg_editor_styles.scss';

const MAX_IMAGE_WIDTH = "400px";
const STATIC_TOOLBAR_SETTINGS = {
  options: [
    'inline',
    'blockType',
    'fontSize',
    'list',
    'textAlign',
    'link',
    'image',
    'history'
  ],
  inline: {
    options: [
      'bold',
      'italic',
      'underline'
    ]
  },
  link: {
    showOpenOptionOnHover: false
  },
  list: {
    options: [
      'unordered',
      'ordered'
    ]
  }
};

export default class WysiwygEditor extends React.Component {
  static propTypes = {
    content: PropTypes.string,
    onChange: PropTypes.func,
    onUploadImage: PropTypes.func
  }

  intitalEditorState = (content) => {
    if (!content) return EditorState.createEmpty();

    try {
      const json = JSON.parse(content);
      return EditorState.createWithContent(convertFromRaw(json));
    } catch (e) {
      return EditorState.createWithContent(ContentState.createFromText(content));
    }
  }

  constructor(props) {
    super(props);
    const editorState = this.intitalEditorState(this.props.content);

    this.state = {
      editorState,
      progress: 0,
      uploading: false
    };
  }

  handleUploadProgress = (event) => {
    this.setState({ progress: Math.floor(event.loaded * 100 / event.total) });
  }

  uploadImage = (file) => {
    const config = { onUploadProgress: this.handleUploadProgress };
    return this.props.onUploadImage(file, config).then((res) => {
      const src = res.data.url;
      const { editorState } = this.state;

      const contentState = editorState.getCurrentContent();

      const contentStateWithImage = contentState.createEntity(
        'IMAGE',
        'MUTABLE',
        { src, width: MAX_IMAGE_WIDTH, alignment: 'left' }
      );
      const imageKey = contentStateWithImage.getLastCreatedEntityKey();

      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithImage
      });

      this.handleChange(AtomicBlockUtils.insertAtomicBlock(newEditorState, imageKey, ' '));
    });
  }

  handleDrop = (files) => {
    if (files.length === 0) {
      this.setState({ progress: 0, uploading: false });
      return;
    }
    const file = files.shift();
    this.setState({ progress: 0, uploading: true });
    this.uploadImage(file).then(() => {
      this.handleDrop(files);
    });
  }

  handleChange = (editorState) => {
    this.setState({ editorState });
    const content = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
    this.props.onChange(content);
  }

  handleUpload = (file) => {
    return new Promise((resolve, _reject) => {
      this.uploadImage(file).then(() => {
        resolve();
      });
    });
  }

  renderProgressBar() {
    return <ProgressBar active now={this.state.progress} />;
  }

  handleKeyCommand = (command) => {
    const { editorState } = this.state;

    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      this.handleChange(newState);
      return "handled";
    }

    return "not-handled";
  }

  renderEditor() {
    const toolbarSettings = { ...STATIC_TOOLBAR_SETTINGS, image: { uploadCallback: this.handleUpload } };

    return (
      <Editor
        editorClassName="form-control py-0"
        editorState={this.state.editorState}
        handleKeyCommand={this.handleKeyCommand}
        toolbar={toolbarSettings}
        onEditorStateChange={this.handleChange}
      />
    );
  }

  render() {
    return (
      <Dropzone
        accept="image/*"
        noClick
        onDrop={this.handleDrop}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()}>
            {this.state.uploading ? this.renderProgressBar() : this.renderEditor()}
            <input {...getInputProps()} />
          </div>
        )}
      </Dropzone>

    );
  }
}
